/**
 * Interview Prep (completion plan P5).
 *
 * Prepare (optional), choose, practise, review:
 *
 *   1. Prepare from the job: paste a job description, upload a job requisition
 *      or give a link (where offered). The server reads it as DATA and answers
 *      normalised notes — role, seniority (only if stated), responsibilities,
 *      requirements, skills, likely topics — each marked as read by AI or
 *      edited by the learner, with provenance. The learner checks and edits
 *      them. Saving them as a profile is a separate, explicit choice.
 *   2. Choose a format (behavioural / situational / role-specific).
 *   3. Choose a full interview or a single-question drill on one topic.
 *   4. The SAME coaching session every other practice uses
 *      (CoachingSessionView: Written Chat or Live Conversation, Development with
 *      hints and retries, or Testing), then results against the interview
 *      practice rubric. There is no interview-specific runtime here.
 *
 * Nothing here names a scenario, persona or rubric — the server resolves the
 * authored interview content for the chosen format, and says CONTENT_INCOMPLETE
 * when there is none rather than improvising one. Nothing is kept in the
 * browser.
 */
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  useGetInterviewPrepCatalogQuery,
  useSavePrepProfileMutation,
  useUpdatePrepProfileMutation,
  type InterviewType,
  type InterviewTypeEntry,
  type PrepContext,
  type PrepProfile,
} from '../store/api/interviewPrepApi';
import { CoachingSessionView, type DisplayPersona, type DisplayScenario } from './CoachingPage';
import { INTERVIEW_PRACTICE_NOTICE } from '../components/ConversationResults';
import { PrepSourcePanel } from '../components/interviewPrep/PrepSourcePanel';
import { PrepContextEditor } from '../components/interviewPrep/PrepContextEditor';
import { PrepProfilesPanel } from '../components/interviewPrep/PrepProfilesPanel';
import {
  DEFAULT_PREP_LIMITS,
  buildInterviewPrepRequest,
  drillTopicOptions,
  interviewPrepBlocker,
  type PracticeKind,
} from '../utils/interviewPrep';
import { getErrorMessage } from '../utils/errorHelpers';
import { ConversationEngineOffNotice, useConversationEngineSwitch } from '../components/ConversationEngineOff';

const DEFAULT_LIMITS = { targetRoleMaxChars: 120, jobDescriptionMaxChars: 6000, drillTopicMaxChars: 200 };

const GAP_COPY: Record<NonNullable<InterviewTypeEntry['gap']>, string> = {
  no_authored_scenario: 'Not available yet: no interview scenario has been authored for this type.',
  interviewer_persona_unavailable: 'Not available yet: the interviewer for this type has not been authored.',
};

function displayDifficulty(d: string): DisplayScenario['difficulty'] {
  return d === 'beginner' || d === 'intermediate' ? d : 'advanced';
}

interface Started {
  persona: DisplayPersona;
  scenario: DisplayScenario;
  interviewPrep: { request: ReturnType<typeof buildInterviewPrepRequest>; testingAvailable: boolean };
}

export function InterviewPrepPage() {
  const engine = useConversationEngineSwitch();
  const { data: catalog, isLoading, isError } = useGetInterviewPrepCatalogQuery(undefined, {
    skip: engine.loading || engine.off,
  });
  const [interviewType, setInterviewType] = useState<InterviewType | null>(null);
  const [targetRole, setTargetRole] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [prep, setPrep] = useState<PrepContext | null>(null);
  const [droppedItems, setDroppedItems] = useState(0);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [profileLabel, setProfileLabel] = useState('');
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [practiceKind, setPracticeKind] = useState<PracticeKind>('interview');
  const [drillTopic, setDrillTopic] = useState('');
  const [started, setStarted] = useState<Started | null>(null);
  const [saveProfile, saveState] = useSavePrepProfileMutation();
  const [updateProfile, updateState] = useUpdatePrepProfileMutation();

  const limits = { ...DEFAULT_LIMITS, ...(catalog?.limits ?? {}) };
  const prepLimits = catalog?.limits.prep ?? DEFAULT_PREP_LIMITS;
  const profilesEnabled = catalog?.profilesEnabled === true;
  const types = catalog?.types ?? [];
  const entry = useMemo(() => types.find((t) => t.interviewType === interviewType), [types, interviewType]);
  const anyAvailable = types.some((t) => t.status === 'available');
  const topics = drillTopicOptions(prep);
  const blocker = interviewPrepBlocker(
    { interviewType, targetRole, jobDescription, practiceKind, drillTopic, prep },
    entry,
    limits,
    prepLimits
  );

  const applyPrepared = (context: PrepContext, dropped: number) => {
    setPrep(context);
    setDroppedItems(dropped);
    setProfileId(null);
    setProfileMessage(null);
    if (!targetRole.trim() && context.roleTitle) setTargetRole(context.roleTitle.value.slice(0, limits.targetRoleMaxChars));
    if (!profileLabel.trim() && context.roleTitle) setProfileLabel(context.roleTitle.value.slice(0, 120));
  };

  const selectProfile = (p: PrepProfile | null) => {
    setProfileMessage(null);
    if (!p) {
      setProfileId(null);
      return;
    }
    setPrep(p.context);
    setDroppedItems(0);
    setProfileId(p.id);
    setProfileLabel(p.label);
    if (!targetRole.trim() && p.context.roleTitle) setTargetRole(p.context.roleTitle.value.slice(0, limits.targetRoleMaxChars));
  };

  const handleSaveProfile = async () => {
    if (!prep) return;
    setProfileMessage(null);
    try {
      if (profileId) {
        await updateProfile({ id: profileId, label: profileLabel, context: prep }).unwrap();
        setProfileMessage('Saved.');
      } else {
        const saved = await saveProfile({ label: profileLabel, context: prep }).unwrap();
        setProfileId(saved.id);
        setProfileMessage('Saved. Practice you start now is grouped under this profile.');
      }
    } catch (e) {
      setProfileMessage(getErrorMessage(e, 'Could not save this preparation.'));
    }
  };

  const handleStart = () => {
    if (blocker || !entry || !entry.scenario || !entry.interviewer || !interviewType) return;
    setStarted({
      persona: {
        // The persona KEY: the server checks it against the scenario's own.
        id: entry.interviewer.personaKey,
        name: entry.interviewer.label,
        role: 'Interviewer',
        description: entry.scenario.description,
        personality: [],
        difficulty: 'medium',
      },
      scenario: {
        id: entry.scenario.id,
        title: entry.scenario.title,
        description: entry.scenario.description,
        category: 'Interview',
        difficulty: displayDifficulty(entry.scenario.difficulty),
        duration: practiceKind === 'question_drill' ? '5 min' : `${entry.scenario.estimatedMinutes} min`,
      },
      interviewPrep: {
        request: buildInterviewPrepRequest({ interviewType, targetRole, jobDescription, prep, practiceKind, drillTopic, prepProfileId: profileId }),
        testingAvailable: entry.testingAvailable,
      },
    });
  };

  if (engine.loading) return null;
  if (engine.off) {
    return (
      <div className="space-y-8 max-w-3xl">
        <h1 className="text-2xl font-bold text-gray-900">Interview Prep</h1>
        <ConversationEngineOffNotice feature="Interview Prep" />
      </div>
    );
  }

  if (started) {
    return (
      <CoachingSessionView
        persona={started.persona}
        scenario={started.scenario}
        interviewPrep={started.interviewPrep}
        onEnd={() => setStarted(null)}
      />
    );
  }

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Interview Prep</h1>
        <p className="mt-1 text-gray-500">
          Practise answering interview questions with an AI interviewer, then get feedback on your answers.
        </p>
        <p className="mt-3 text-sm text-gray-600">{INTERVIEW_PRACTICE_NOTICE}</p>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading interview types…</p>}
      {isError && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
          <p className="text-sm text-amber-900">Interview Prep is not available right now.</p>
        </div>
      )}

      {catalog && !anyAvailable && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-4" data-status="CONTENT_INCOMPLETE">
          <p className="text-sm font-medium text-gray-900">Interview practice content is still being authored.</p>
          <p className="mt-1 text-sm text-gray-600">
            No interviewer or interview scenario has been published yet, so practice cannot start. Check back soon.
          </p>
        </div>
      )}

      {catalog && (
        <>
          <section className="space-y-4" data-testid="prep-section">
            <h2 className="text-lg font-semibold text-gray-900">1. Prepare from the job (optional)</h2>
            <p className="text-sm text-gray-600">
              Give BeHeard the job description and it will draft notes about the role for you to check. The text is read once and not kept.
            </p>
            {profilesEnabled && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Your saved preparation</h3>
                <PrepProfilesPanel selectedId={profileId} onSelect={selectProfile} />
              </div>
            )}
            <PrepSourcePanel catalog={catalog} onPrepared={(p) => applyPrepared(p.context, p.guard.droppedItems)} />
            {prep && (
              <div className="rounded-lg border border-gray-200 p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Your notes for this role</h3>
                  <button type="button" className="text-xs text-gray-500 hover:text-gray-800" onClick={() => { setPrep(null); setProfileId(null); }}>
                    Clear
                  </button>
                </div>
                <PrepContextEditor context={prep} onChange={setPrep} limits={prepLimits} droppedItems={droppedItems} />
                {profilesEnabled && (
                  <div className="border-t border-gray-100 pt-3" data-testid="prep-save">
                    <label htmlFor="prep-profile-label" className="block text-sm font-medium text-gray-700">
                      Save these notes to come back to them (optional)
                    </label>
                    <div className="mt-1 flex gap-2">
                      <input
                        id="prep-profile-label"
                        type="text"
                        value={profileLabel}
                        maxLength={catalog.limits.profileLabelMaxChars ?? 120}
                        onChange={(e) => setProfileLabel(e.target.value)}
                        placeholder="e.g. Analyst role at a payments company"
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleSaveProfile}
                        disabled={!profileLabel.trim() || saveState.isLoading || updateState.isLoading}
                        className="btn-secondary px-4 py-2 text-sm disabled:opacity-50"
                      >
                        {profileId ? 'Save changes' : 'Save'}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Only the notes are saved — not the job description, file or link. Your attempts are then grouped under it so you can see your progress.
                    </p>
                    {profileMessage && <p className="mt-1 text-xs text-gray-700" role="status">{profileMessage}</p>}
                  </div>
                )}
              </div>
            )}
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">2. Choose an interview type</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" role="radiogroup" aria-label="Interview type">
              {types.map((t) => {
                const disabled = t.status !== 'available';
                const selected = interviewType === t.interviewType;
                return (
                  <button
                    key={t.interviewType}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={disabled}
                    onClick={() => setInterviewType(t.interviewType)}
                    className={clsx(
                      'text-left rounded-lg border p-4 transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                      selected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <div className="font-medium text-gray-900">{t.label}</div>
                    <p className="mt-1 text-xs text-gray-600">{t.description}</p>
                    {disabled && t.gap && <p className="mt-2 text-xs text-gray-500">{GAP_COPY[t.gap]}</p>}
                    {!disabled && t.interviewer && (
                      <p className="mt-2 text-xs text-gray-500">Interviewer: {t.interviewer.label}</p>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">3. Full interview or one question?</h2>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Practice kind">
              {(['interview', 'question_drill'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={practiceKind === k}
                  onClick={() => setPracticeKind(k)}
                  className={clsx(
                    'rounded-lg border px-3 py-2 text-sm',
                    practiceKind === k ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  {k === 'interview' ? 'Full interview' : 'Single-question drill'}
                </button>
              ))}
            </div>
            {practiceKind === 'question_drill' && (
              <div data-testid="drill-topic">
                <label htmlFor="ip-drill" className="block text-sm font-medium text-gray-700">Drill topic</label>
                {topics.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {topics.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setDrillTopic(t.slice(0, limits.drillTopicMaxChars))}
                        className={clsx('rounded-full border px-2.5 py-1 text-xs', drillTopic === t ? 'border-primary-500 bg-primary-50' : 'border-gray-200')}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                )}
                <input
                  id="ip-drill"
                  type="text"
                  value={drillTopic}
                  maxLength={limits.drillTopicMaxChars}
                  onChange={(e) => setDrillTopic(e.target.value)}
                  placeholder="e.g. Handling a missed deadline"
                  className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                />
              </div>
            )}
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">4. Anything else about the role (optional)</h2>
            <div>
              <label htmlFor="ip-role" className="block text-sm font-medium text-gray-700">Target role</label>
              <input
                id="ip-role"
                type="text"
                value={targetRole}
                maxLength={limits.targetRoleMaxChars}
                onChange={(e) => setTargetRole(e.target.value)}
                placeholder="e.g. Customer success manager"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="ip-jd" className="block text-sm font-medium text-gray-700">Job description</label>
              <textarea
                id="ip-jd"
                value={jobDescription}
                maxLength={limits.jobDescriptionMaxChars}
                onChange={(e) => setJobDescription(e.target.value)}
                rows={5}
                placeholder="Optional: paste the job description to send it with this practice session as well."
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
              />
              <div className="mt-1 flex justify-between text-xs text-gray-500">
                <span>Used only for this practice session. It is not added to your profile or shared.</span>
                <span className="tabular-nums">{jobDescription.length}/{limits.jobDescriptionMaxChars}</span>
              </div>
            </div>
          </section>

          <div className="flex items-center justify-between gap-4">
            <p className="text-xs text-gray-500">
              You can choose Written Chat or Live Conversation, and Practice (with hints and retries) or Test, on the next screen.
            </p>
            <button
              type="button"
              onClick={handleStart}
              disabled={!!blocker}
              title={blocker ?? undefined}
              className="btn-primary px-6 py-3 text-base disabled:opacity-50"
            >
              {practiceKind === 'question_drill' ? 'Start drill' : 'Start interview practice'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default InterviewPrepPage;
